/* panel-focus-lock.tsx generated by @compiled/babel-plugin v3.0.2 */
import { ax, ix } from "@compiled/react/runtime";
require("@compiled/webpack-loader/css-loader!@compiled/webpack-loader/css-loader/compiled-css.css?style=._0jFU0VN91d%7Bheight%3A100%25%7D");
import React, { type ReactNode, useEffect, useRef } from 'react';
import FocusLock from 'react-focus-lock';
import { getDocument } from '@atlaskit/browser-apis';
// display: contents makes the wrapper div layout-transparent — it disappears from the
// box model so PanelContainer's own height/flex styles work exactly as if this wrapper
// were not there. This avoids breaking resize and full-height layouts.
const contentsStyle = ({
  display: 'contents'
} as const);
const containerStyles = null;

/**
 * Module-level count of currently-mounted PanelFocusLock instances.
 *
 * Used to suppress the return-focus restore when one panel is replaced by
 * another (e.g. Jira admin sidebar swaps Sidebar A → Sidebar B by changing
 * the Provider component, which unmounts/remounts PanelFocusLock). Without
 * this guard, the unmounting panel's queued double-rAF restore would fire
 * AFTER the new panel's autofocus, snapping focus back to the original
 * trigger while a replacement panel is on screen.
 *
 * The counter is decremented synchronously on cleanup BEFORE the new
 * instance's mount effect runs, then re-checked inside the deferred restore.
 * If another instance has mounted by the time the double-rAF fires, the
 * restore is skipped.
 */
let panelFocusLockMountCount = 0;

/**
 * Module-level handoff for the "previously focused" element across an
 * unmount → mount swap.
 *
 * When one PanelFocusLock unmounts and is immediately replaced by another
 * because a button INSIDE the unmounting panel triggered the swap (e.g.
 * Sidebar A's footer "Add spaces" button opens Sidebar B), the replacement's
 * mount effect sees `document.activeElement === document.body` because the
 * previously-focused element lived inside the panel that just unmounted.
 * Without a handoff, the replacement would capture `body` as its "previously
 * focused" snapshot and on close would restore focus to nothing / blur.
 *
 * The unmounting instance writes its own previously-focused snapshot here,
 * and the next mounting instance reads + clears it. The handoff is used
 * ONLY when the replacement's `document.activeElement` is `body` (or null) —
 * meaning focus disappeared because it lived inside the unmounting panel.
 * If `document.activeElement` is a real element (e.g. the user clicked a
 * different page-level trigger to open this panel), that element wins
 * because it's a more accurate "where focus came from" signal than the
 * previous panel's snapshot. Cleared either by the next mount or by the
 * deferred restore when no replacement appears, so a stale value can't leak
 * across unrelated open cycles.
 */
let pendingPreviouslyFocused: HTMLElement | null = null;

/**
 * Selector for all focusable elements inside a container, in LTR tab order.
 */
const FOCUSABLE_SELECTOR = ['a[href]', 'button:not([disabled])', 'input:not([disabled])', 'select:not([disabled])', 'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])', 'details > summary', 'audio[controls]', 'video[controls]'].join(', ');
export type PanelFocusLockProps = {
  /**
   * Content rendered inside the focus-managed panel.
   */
  children: ReactNode;

  /**
   * When `true`, focus trap is enabled so that tabbing will only cycle through elements within the panel.
   * Use only when the panel is blocking/overlaying the main content, usually with a blanket.
   *
   * @default false
   */
  isFocusLockEnabled?: boolean;
};

/**
 * __PanelFocusLock__
 *
 * Provides focus management for panel-system panels:
 * - Always moves focus to the first interactive element (via react-focus-lock's autoFocus) when the panel opens.
 * - Optionally traps focus inside the panel via `isFocusLockEnabled`.
 * - Returns focus to the previously focused element when the panel closes.
 *
 * This is baked into `PanelContainer`.
 *
 * - [Examples](https://atlassian.design/components/panel-system/examples)
 * - [Code](https://atlassian.design/components/panel-system/code)
 */
export function PanelFocusLock({
  children,
  isFocusLockEnabled = false
}: PanelFocusLockProps): React.JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);

  // Capture the element that was focused before the panel opened so we can
  // restore focus to it when the panel closes — regardless of which close
  // path is taken (X button, footer Close, programmatic close, etc.).
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    // Determine the element focus should return to when this panel closes.
    //
    // Default: use the element that was focused at the moment we mount —
    // this is the natural "page trigger" the user just clicked.
    //
    // Edge case (handoff): when one panel unmounts and is immediately
    // replaced by another because a button INSIDE the previous panel
    // triggered the swap (e.g. Sidebar A's footer "Add spaces" button
    // opens Sidebar B), focus has nowhere to go: the trigger button just
    // unmounted with the previous panel, so `document.activeElement`
    // falls back to `body`. In that specific case we adopt the previous
    // panel's snapshot via the module-level handoff slot — it's typically
    // the original page-level trigger that opened the chain.
    //
    // Edge case (StrictMode): in development, React double-invokes
    // effects (setup → cleanup → setup). The second setup runs AFTER
    // Effect 2 below has already moved focus to our own first focusable,
    // so `document.activeElement` is inside our own container — but the
    // DOM was never actually unmounted, so the focused element is still
    // attached. We treat this case the same as body: the user never
    // "came from" our own input, so we should fall back to the handoff
    // (which was just set by Pass 1's cleanup). Without this guard,
    // `previouslyFocused` would be set to our own first focusable, and
    // on close `focus()` would target an element that is by then detached.
    //
    // Always clear the handoff slot regardless of whether we used it, so
    // a stale value can't leak forward into an unrelated future mount.
    const doc = getDocument();
    const handoff = pendingPreviouslyFocused;
    pendingPreviouslyFocused = null;
    const activeElement = (doc?.activeElement as HTMLElement | null) ?? null;
    const activeIsBodyOrNull = !activeElement || activeElement === doc?.body;
    const activeIsInsideOwnContainer = !!activeElement && !!containerRef.current?.contains(activeElement);
    const shouldUseHandoff = activeIsBodyOrNull || activeIsInsideOwnContainer;
    const handoffIsAttached = !!handoff && !!doc?.body?.contains(handoff);
    previouslyFocusedRef.current = shouldUseHandoff && handoffIsAttached ? handoff : activeElement;
    panelFocusLockMountCount += 1;
    return () => {
      panelFocusLockMountCount -= 1;
      // Hand our snapshot off in case a replacement instance is about to
      // mount in the same commit — it will consume this and use it as its
      // own return-focus target, preserving the original trigger across the
      // swap. The slot is cleared either by the next mount or by the
      // deferred restore below if no replacement appears.
      pendingPreviouslyFocused = previouslyFocusedRef.current;

      // Capture the target locally so it survives even if something else
      // mutates the ref between now and when the deferred callback fires.
      const toFocus = previouslyFocusedRef.current;

      // Restore focus on unmount (panel close). Two chained `setTimeout(0)`
      // ensure this fires after react-focus-lock's own deactivation logic
      // when the lock is active, and after React has finished applying any
      // commit that mounted a replacement panel.
      //
      // We use setTimeout rather than requestAnimationFrame here because
      // the latter is unreliable under jsdom + the React 18 concurrent
      // scheduler in unit tests: rAF callbacks scheduled inside a passive
      // effect cleanup don't always flush even when `jest.runAllTimers`
      // is called inside an `act` block (the scheduler uses MessageChannel,
      // which fake timers don't drive). setTimeout(0) flushes reliably with
      // both fake and real timers.
      setTimeout(() => {
        setTimeout(() => {
          // Skip restore if another PanelFocusLock instance has mounted in
          // the meantime — e.g. a sidebar swap where the consumer unmounts
          // the current panel and mounts a replacement. The new instance's
          // autofocus already moved focus into the replacement; restoring
          // here would yank focus back to this panel's previously-focused
          // element (typically the original page trigger) and visibly steal
          // focus away from the replacement panel.
          if (panelFocusLockMountCount > 0) {
            return;
          }
          // We're the last one out — clear the handoff so a future,
          // unrelated panel open doesn't inherit a stale snapshot.
          pendingPreviouslyFocused = null;
          toFocus?.focus();
        }, 0);
      }, 0);
    };
  }, []);

  // When the lock is NOT enabled, handle auto-focus ourselves — do NOT use
  // react-focus-lock even in disabled mode as it causes layout recalculations
  // that causes animation jank when the panel is inside a CSS transform animation
  // (e.g. SlideIn from @atlaskit/motion).
  useEffect(() => {
    if (isFocusLockEnabled) {
      return;
    }
    const container = containerRef.current;
    if (!container) {
      return;
    }
    // Focus synchronously in useEffect (post-paint) with preventScroll to avoid
    // the browser calling scrollIntoView on a mid-animation element.
    const firstFocusable = container.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
    if (firstFocusable) {
      firstFocusable.focus({
        preventScroll: true
      });
    }
  }, [isFocusLockEnabled]);

  // When the lock IS enabled, use react-focus-lock for both autoFocus and the trap.
  if (isFocusLockEnabled) {
    return <FocusLock autoFocus returnFocus={false}
    // eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
    lockProps={{
      style: contentsStyle
    }}>
				{/* eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop */}
				<div ref={containerRef} style={contentsStyle}>
					{children}
				</div>
			</FocusLock>;
  }

  // Default (no lock): plain wrapper div, auto-focus handled by useEffect above.
  // We deliberately avoid react-focus-lock here to prevent animation jank.
  // height: 100% ensures PanelContainer's own height: 100% resolves correctly.
  // eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
  return <div ref={containerRef} className={ax(["_0jFU0VN91d"])}>
			{children}
		</div>;
}